import { UserEventMessage, UserEventMessageType } from '@playcastdotio/Messaging';
import { updateState } from '../../userEvents';

// Store for pending requests with correlation IDs
const pendingRequests = new Map<
  string,
  {
    resolve: (value?: any) => void;
    reject: (error: Error) => void;
    timeout: NodeJS.Timeout;
  }
>();

/**
 * Register a pending request with correlation ID
 * Used internally by userEvents functions to track responses
 */
export const registerPendingRequest = (
  correlationId: string,
  resolve: (value?: any) => void,
  reject: (error: Error) => void,
  timeoutMs = 10000,
): void => {
  // Clear any existing timeout for this correlation ID
  const existing = pendingRequests.get(correlationId);
  if (existing) {
    clearTimeout(existing.timeout);
  }

  // Set up timeout
  const timeout = setTimeout(() => {
    const request = pendingRequests.get(correlationId);
    if (request) {
      pendingRequests.delete(correlationId);
      request.reject(new Error('Request timed out'));
    }
  }, timeoutMs);

  // Store the request
  pendingRequests.set(correlationId, {
    resolve,
    reject,
    timeout,
  });
};

/**
 * Handle incoming user event messages
 * This processes responses to user event requests (ACK, ERROR, FETCH_RESPONSE)
 */
export const handleUserEventsMessage = (message: UserEventMessage): void => {
  console.log('User events message:', message);

  switch (message.type) {
    case UserEventMessageType.USEREVENT_ACK: {
      console.log('User event acknowledged:', message);

      if (message.correlationId) {
        const request = pendingRequests.get(message.correlationId);
        if (request) {
          clearTimeout(request.timeout);
          pendingRequests.delete(message.correlationId);
          request.resolve(message);
        }
      }
      break;
    }

    case UserEventMessageType.USEREVENT_ERROR: {
      console.error('User event error:', message);

      if (message.correlationId) {
        const request = pendingRequests.get(message.correlationId);
        if (request) {
          clearTimeout(request.timeout);
          pendingRequests.delete(message.correlationId);
          const errorMessage = message.error?.message || 'Unknown error occurred';
          request.reject(new Error(errorMessage));
        }
      }
      break;
    }

    case UserEventMessageType.USEREVENT_FETCH_RESPONSE: {
      console.log('User events fetch response:', message);

      if (message.correlationId) {
        const request = pendingRequests.get(message.correlationId);
        if (request) {
          clearTimeout(request.timeout);
          pendingRequests.delete(message.correlationId);
          request.resolve(message);
        }
      }
      break;
    }

    case UserEventMessageType.USEREVENT_SERVER_PUSH: {
      // Update the user events state
      updateState(message);
      break;
    }

    default: {
      console.warn('Unknown user event message type:', message.type);
    }
  }
};

/**
 * Clean up any expired pending requests
 * This should be called periodically to prevent memory leaks
 */
export const cleanupExpiredRequests = (): void => {
  // The individual timeouts will handle cleanup, but this provides
  // an additional safety net if needed
  console.log(`Active pending requests: ${pendingRequests.size}`);
};
